import React, { useEffect, useState } from "react";
import {
  fetchMyWallet,
  submitWithdrawal,
  startConnectOnboarding,
  fetchConnectStatus,
  fetchConnectLoginLink,
} from "../lib/api";
import { useFeatureFlags } from "../context/FeatureFlagsContext";

/**
 * WalletPanel — shows the player's tip balance, ledger and the
 * withdrawal-request form. Fully hidden when the global
 * `tips_enabled` feature flag is off, so the surface never even
 * mounts until the operator activates creator payouts from admin.
 */
export function WalletPanel({ onToast }) {
  const { flags } = useFeatureFlags();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    if (!flags?.tips_enabled) { setData(null); setLoading(false); return; }
    setLoading(true);
    try {
      setData(await fetchMyWallet());
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't load wallet.");
    } finally { setLoading(false); }
  }, [flags?.tips_enabled, onToast]);

  useEffect(() => { load(); }, [load]);

  if (!flags?.tips_enabled) return null;
  if (loading) return (
    <div className="text-[12px] text-white/40 p-4">Loading wallet…</div>
  );
  if (!data) return null;

  const { wallet, ledger = [], withdrawals = [] } = data;

  return (
    <div data-testid="wallet-panel" className="space-y-5">
      <div className="rounded-2xl p-5 border border-[#14b8a6]/30 bg-gradient-to-br from-[#0b1a18] via-[#0a2724] to-[#0b1a18]">
        <p className="text-[10px] tracking-[4px] text-[#14b8a6] font-bold">
          <i className="fas fa-wallet mr-1" />TIP WALLET
        </p>
        <p
          className="mt-2 text-4xl font-black text-white"
          data-testid="wallet-balance"
        >
          ${Number(wallet?.balance_usd || 0).toFixed(2)}
        </p>
        <p className="text-[11px] text-white/50">
          Available to withdraw. Minimum: ${Number(wallet?.min_withdrawal_usd || 25).toFixed(2)} ·
          {" "}{Number(wallet?.manual_payout_surcharge_pct || 5).toFixed(0)}% processing fee on PayPal / IBAN / Crypto · Stripe Connect is fee-free.
        </p>
      </div>

      <ConnectOnboardCard onToast={onToast} />

      <WithdrawForm
        max={Number(wallet?.balance_usd || 0)}
        min={Number(wallet?.min_withdrawal_usd || 25)}
        methods={data.methods || ["paypal", "iban", "crypto", "stripe_connect"]}
        onSubmitted={() => { onToast?.("Withdrawal submitted."); load(); }}
        onToast={onToast}
      />

      <Section title="WITHDRAWAL HISTORY" testid="withdrawal-history">
        {withdrawals.length === 0 ? (
          <p className="text-[11px] text-white/40">No withdrawals yet.</p>
        ) : (
          <ul className="space-y-1">
            {withdrawals.map((w) => (
              <li key={w.withdrawal_id}
                  data-testid={`wd-row-${w.withdrawal_id}`}
                  className="flex items-center justify-between p-2 rounded-xl bg-black/30 border border-white/10 text-[12px]">
                <span className="font-bold">${Number(w.gross_amount_usd).toFixed(2)}</span>
                <span className="text-white/50 uppercase">{w.method}</span>
                <span className={`text-[10px] tracking-widest font-bold ${statusColor(w.status)}`}>
                  {w.status?.toUpperCase()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="LEDGER (RECENT)" testid="ledger">
        {ledger.length === 0 ? (
          <p className="text-[11px] text-white/40">No activity yet — your tips will appear here.</p>
        ) : (
          <ul className="space-y-1">
            {ledger.slice(0, 12).map((l) => (
              <li key={l.entry_id}
                  className="flex items-center justify-between p-2 rounded-xl bg-black/20 text-[12px]">
                <span className="text-white/70 uppercase tracking-wider text-[10px]">{l.kind?.replace(/_/g, " ")}</span>
                <span className={`font-bold ${l.amount_usd >= 0 ? "text-[#14b8a6]" : "text-amber-300"}`}>
                  {l.amount_usd >= 0 ? "+" : ""}${Number(l.amount_usd).toFixed(2)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function Section({ title, testid, children }) {
  return (
    <div data-testid={testid} className="rounded-2xl p-4 border border-white/10 bg-black/20">
      <p className="text-[10px] tracking-[3px] font-bold text-white/60 mb-2">{title}</p>
      {children}
    </div>
  );
}

function statusColor(status) {
  if (status === "paid") return "text-[#14b8a6]";
  if (status === "rejected") return "text-red-400";
  if (status === "approved") return "text-amber-300";
  return "text-white/60";
}

function WithdrawForm({ max, min, methods, onSubmitted, onToast }) {
  const [method, setMethod] = useState(methods[0] || "paypal");
  const [destination, setDestination] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const isStripeConnect = method === "stripe_connect";

  const submit = async () => {
    const a = parseFloat(amount);
    if (Number.isNaN(a) || a < min) { onToast?.(`Amount must be ≥ $${min}.`); return; }
    if (a > max) { onToast?.(`Insufficient balance.`); return; }
    if (!isStripeConnect && destination.trim().length < 4) {
      onToast?.("Destination looks incomplete.");
      return;
    }
    setBusy(true);
    try {
      // Stripe Connect uses the cached account on the server — destination
      // payload is ignored. Send a placeholder to keep the API contract
      // simple. Backend looks up the real Connect Account ID.
      const dest = isStripeConnect ? "stripe_connect" : destination.trim();
      await submitWithdrawal(method, dest, a);
      setDestination(""); setAmount("");
      onSubmitted?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Withdrawal failed.");
    } finally { setBusy(false); }
  };

  const placeholder =
    method === "paypal" ? "PayPal email" :
    method === "iban"   ? "IBAN"          :
    method === "crypto" ? "Wallet address" :
    /* stripe_connect */ "";

  return (
    <div className="rounded-2xl p-4 border border-[#14b8a6]/30 bg-black/30 space-y-3"
         data-testid="withdraw-form">
      <p className="text-[10px] tracking-[3px] font-bold text-[#14b8a6]">
        <i className="fas fa-arrow-right-from-bracket mr-1" />WITHDRAW
      </p>
      <div className="flex gap-2 flex-wrap">
        {methods.map((m) => (
          <button key={m}
                  type="button"
                  onClick={() => setMethod(m)}
                  data-testid={`withdraw-method-${m}`}
                  className={`px-3 py-1.5 rounded-xl text-[11px] tracking-widest font-bold border ${
                    method === m
                      ? "bg-[#14b8a6]/20 border-[#14b8a6] text-[#14b8a6]"
                      : "bg-black/30 border-white/10 text-white/60 hover:border-[#14b8a6]/40"
                  }`}>
            {m === "stripe_connect" ? "STRIPE · AUTO" : m.toUpperCase()}
          </button>
        ))}
      </div>
      {isStripeConnect ? (
        <p className="text-[11px] text-white/55 leading-snug" data-testid="withdraw-stripe-hint">
          <i className="fas fa-bolt mr-1 text-[#14b8a6]" />
          Funds are pushed directly to your connected Stripe Express account.
          No processing fee. Requires Stripe onboarding above.
        </p>
      ) : (
        <input type="text"
               placeholder={placeholder}
               value={destination}
               onChange={(e) => setDestination(e.target.value)}
               data-testid="withdraw-destination"
               className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm" />
      )}
      <input type="number"
             placeholder={`Amount (min $${min})`}
             min={min}
             max={max}
             step="0.01"
             value={amount}
             onChange={(e) => setAmount(e.target.value)}
             data-testid="withdraw-amount"
             className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-white text-sm" />
      <button type="button"
              onClick={submit}
              disabled={busy}
              data-testid="withdraw-submit-btn"
              className="w-full rounded-xl bg-gradient-to-r from-[#14b8a6] to-[#0d9488] text-black text-xs tracking-widest font-bold py-2.5 hover:brightness-110 disabled:opacity-50">
        {busy ? "…" : "SUBMIT REQUEST"}
      </button>
    </div>
  );
}

/**
 * ConnectOnboardCard — Stripe Express onboarding entry point that lives
 * above the WithdrawForm. Three states:
 *   • Never connected  → CONNECT STRIPE CTA (opens hosted onboarding)
 *   • Onboarding done  → green chip + "OPEN STRIPE DASHBOARD" button
 *   • Pending (account created but `payouts_enabled=false`) → "FINISH SETUP"
 *
 * Hides itself once the wallet feature flag is off (parent already gates).
 */
function ConnectOnboardCard({ onToast }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const reload = React.useCallback(async () => {
    try {
      setStatus(await fetchConnectStatus());
    } catch (e) {
      // Endpoint may 401 right at boot — just skip silently.
    }
  }, []);

  useEffect(() => {
    reload();
    // After return from Stripe (?onboarded=ok) auto-refresh.
    if (typeof window !== "undefined" && /onboarded=/.test(window.location.search)) {
      reload();
    }
  }, [reload]);

  const connect = async () => {
    setBusy(true);
    try {
      const r = await startConnectOnboarding(window.location.origin);
      if (r?.url) window.location.assign(r.url);
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't start Stripe onboarding.");
    } finally { setBusy(false); }
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const r = await fetchConnectLoginLink();
      if (r?.url) window.open(r.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't open dashboard.");
    } finally { setBusy(false); }
  };

  if (!status) return null;

  const ready = status.charges_enabled && status.payouts_enabled;
  const inProgress = status.connected && !ready;

  return (
    <div data-testid="connect-onboard-card"
         className="rounded-2xl p-4 border border-violet-400/30 bg-gradient-to-br from-[#1a0f2b] via-[#150a26] to-[#1a0f2b]">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] tracking-[3px] font-bold text-violet-300">
            <i className="fab fa-stripe-s mr-1" />STRIPE CONNECT · AUTO PAYOUTS
          </p>
          <p className="text-[11px] text-white/60 mt-1 leading-snug">
            {ready
              ? <>Connected & verified. Approved withdrawals are wired straight to your Stripe account.</>
              : inProgress
                ? <>Almost done — finish your Stripe verification to unlock automatic payouts.</>
                : <>Connect a Stripe Express account to receive payouts instantly with zero processing fee.</>}
          </p>
        </div>
        {ready ? (
          <span data-testid="connect-status-ready"
                className="text-[9px] tracking-widest font-bold px-2.5 py-1 rounded-full bg-[#14b8a6]/15 border border-[#14b8a6]/45 text-[#14b8a6] shrink-0">
            <i className="fas fa-circle-check mr-1" />ACTIVE
          </span>
        ) : null}
      </div>
      <div className="mt-3 flex gap-2 flex-wrap">
        {ready ? (
          <button type="button"
                  onClick={openDashboard}
                  disabled={busy}
                  data-testid="connect-dashboard-btn"
                  className="text-[10px] tracking-widest font-bold px-3 py-2 rounded-xl bg-violet-500/15 border border-violet-400/45 text-violet-200 hover:bg-violet-500/25 disabled:opacity-50">
            <i className="fas fa-up-right-from-square mr-1" />OPEN STRIPE DASHBOARD
          </button>
        ) : (
          <button type="button"
                  onClick={connect}
                  disabled={busy}
                  data-testid="connect-start-btn"
                  className="text-[10px] tracking-widest font-bold px-3 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-violet-700 text-white hover:brightness-110 disabled:opacity-50">
            {busy
              ? "…"
              : inProgress
                ? <><i className="fas fa-arrow-right mr-1" />FINISH SETUP</>
                : <><i className="fab fa-stripe-s mr-1" />CONNECT STRIPE</>}
          </button>
        )}
      </div>
    </div>
  );
}
